import React from 'react';
import { TrendingUp, DollarSign, BarChart3, AlertTriangle } from 'lucide-react';
import { BENCHMARKS } from '../constants/benchmarks';

interface RecommendationsProps {
  metrics: {
    roi: number;
    costPerLead: number;
    cacTicketRatio: number;
    conversionRate: number;
    acquisitionChannels: string[];
  };
}

export function Recommendations({ metrics }: RecommendationsProps) {
  const getROIRecommendation = () => {
    if (metrics.roi >= BENCHMARKS.roi.ultraPerformance) {
      return {
        icon: <TrendingUp className="h-5 w-5 text-blue-500 mt-1" />,
        text: "ROI excelente! Mantenha a estratégia atual e considere:",
        bullets: [
          "Aumentar gradualmente o investimento em marketing",
          "Explorar novos canais mantendo a eficiência",
          "Documentar as práticas de sucesso"
        ]
      };
    } else if (metrics.roi >= BENCHMARKS.roi.target) {
      return {
        icon: <TrendingUp className="h-5 w-5 text-green-500 mt-1" />,
        text: "ROI dentro da meta. Para alcançar ultra performance:",
        bullets: [
          "Otimizar campanhas com melhor desempenho",
          "Analisar jornada do cliente para identificar pontos de melhoria",
          "Testar novas abordagens de forma controlada"
        ]
      };
    }
    return {
      icon: <AlertTriangle className="h-5 w-5 text-yellow-500 mt-1" />,
      text: "ROI abaixo da meta. Ações recomendadas:",
      bullets: [
        "Revisar estratégia de preços e custos",
        "Focar em canais com melhor performance",
        "Avaliar processo de qualificação de leads"
      ]
    };
  };

  const getConversionRecommendation = () => {
    if (metrics.conversionRate >= BENCHMARKS.conversion.target) {
      return {
        icon: <TrendingUp className="h-5 w-5 text-blue-500 mt-1" />,
        text: "Ótima taxa de conversão! Próximos passos:",
        bullets: [
          "Documentar processo de vendas atual",
          "Treinar novos vendedores com base nas melhores práticas",
          "Implementar sistema de feedback contínuo"
        ]
      };
    }
    return {
      icon: <AlertTriangle className="h-5 w-5 text-yellow-500 mt-1" />,
      text: "Oportunidade de melhorar conversões:",
      bullets: [
        "Revisar processo de qualificação de leads",
        "Implementar nurturing para leads não convertidos",
        "Analisar objeções comuns nas negociações"
      ]
    };
  };

  const getCostPerLeadRecommendation = () => {
    if (metrics.costPerLead <= BENCHMARKS.costPerLead.target) {
      return {
        icon: <DollarSign className="h-5 w-5 text-blue-500 mt-1" />,
        text: "Custo por lead eficiente. Para manter:",
        bullets: [
          "Monitorar ROI por canal de aquisição",
          "Otimizar campanhas existentes",
          "Testar novas audiências mantendo eficiência"
        ]
      };
    }
    return {
      icon: <DollarSign className="h-5 w-5 text-yellow-500 mt-1" />,
      text: "Custo por lead acima da meta:",
      bullets: [
        "Revisar segmentação das campanhas",
        "Otimizar páginas de conversão",
        "Avaliar qualidade dos leads por canal"
      ]
    };
  };

  const getCACRecommendation = () => {
    if (metrics.cacTicketRatio >= BENCHMARKS.cacTicketRatio.target) {
      return {
        icon: <BarChart3 className="h-5 w-5 text-blue-500 mt-1" />,
        text: "Excelente relação Ticket/CAC! Recomendações:",
        bullets: [
          "Manter mix de serviços atual",
          "Identificar oportunidades de upsell",
          "Documentar casos de sucesso"
        ]
      };
    }
    return {
      icon: <BarChart3 className="h-5 w-5 text-yellow-500 mt-1" />,
      text: "Oportunidade de melhorar relação Ticket/CAC:",
      bullets: [
        "Revisar estratégia de precificação",
        "Analisar mix de serviços",
        "Otimizar processo de vendas"
      ]
    };
  };

  const recommendations = [
    getROIRecommendation(),
    getConversionRecommendation(),
    getCostPerLeadRecommendation(),
    getCACRecommendation(),
  ];

  return (
    <div className="space-y-6">
      {recommendations.map((rec, index) => (
        <div key={index} className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
          <div className="flex items-start gap-3">
            {rec.icon}
            <div>
              <p className="text-gray-700 font-medium mb-2">{rec.text}</p>
              <ul className="list-disc list-inside space-y-1">
                {rec.bullets.map((bullet, i) => (
                  <li key={i} className="text-gray-600 text-sm">{bullet}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ))}

      <div className="bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
        <div className="flex items-start gap-3">
          <BarChart3 className="h-5 w-5 text-blue-500 mt-1" />
          <div>
            <p className="text-gray-700 font-medium mb-2">
              Análise de Canais de Aquisição
            </p>
            <p className="text-gray-600 text-sm mb-2">
              Seus principais canais são: {metrics.acquisitionChannels.join(', ')}
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
              <li>Compare o desempenho individual de cada canal</li>
              <li>Priorize investimentos nos canais mais eficientes</li>
              <li>Considere testar novos canais de forma controlada</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}